import React, { useCallback, useEffect, useState } from 'react';
import {
  Box, Button, Chip, CircularProgress, Collapse, IconButton, Link, LinearProgress,
  Stack, Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import RefreshIcon from '@mui/icons-material/Refresh';
import {
  patreonPostAPI, PatreonPostJob, PatreonEditSeed,
} from '../../services/api';

const jobToSeed = (j: PatreonPostJob): PatreonEditSeed => ({
  patreon_post_id: j.patreon_post_id || '',
  post_type: j.post_type,
  title: j.title,
  body: j.body,
  tags: j.tags,
  tier_ids: j.tier_ids,
  collection: j.collection,
  podcast: j.podcast,
  is_public: j.is_public,
});

// Posts-management panel — the internal record of every Patreon post attempt
// (status, links) so partial/failed drafts can be resumed and live posts edited.

const STATUS_COLOR: Record<string, 'success' | 'error' | 'info' | 'warning' | 'default'> = {
  done: 'success', error: 'error', queued: 'default',
  staging: 'info', uploading: 'info', creating: 'info', finalizing: 'warning',
};

const fmtWhen = (s: string | null) =>
  s ? new Date(s).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }) : '';

const isPartial = (s: string) => !['done', 'error'].includes(s);

const JobRow: React.FC<{ job: PatreonPostJob; onChange: (j: PatreonPostJob) => void; onEdit: (j: PatreonPostJob) => void }> = ({ job, onChange, onEdit }) => {
  const [busy, setBusy] = useState(false);
  const [resumeErr, setResumeErr] = useState<string | null>(null);
  const resume = async () => {
    setBusy(true); setResumeErr(null);
    try { onChange(await patreonPostAPI.retryJob(job.job_id)); }
    catch (e: any) { setResumeErr(e?.response?.data?.detail || e?.message || 'resume failed'); }
    finally { setBusy(false); }
  };
  // poll while a resumed job is running
  useEffect(() => {
    if (!isPartial(job.status)) return;
    const id = setInterval(async () => {
      try {
        const j = await patreonPostAPI.getJob(job.job_id);
        onChange(j);
        if (!isPartial(j.status)) clearInterval(id);
      } catch { /* keep polling */ }
    }, 2500);
    return () => clearInterval(id);
  }, [job.job_id, job.status, onChange]);

  const editUrl = job.patreon_url ? job.patreon_url.replace(/\/?$/, '') + '/edit' : null;
  return (
    <Box sx={{ borderBottom: '1px solid', borderColor: 'divider', py: 0.75 }}>
      <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between">
        <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 0 }}>
          <Chip size="small" label={job.status} color={STATUS_COLOR[job.status] || 'default'}
            sx={{ height: 20 }} />
          <Typography variant="body2" noWrap sx={{ maxWidth: 280 }}>
            {job.title || '(untitled)'}
          </Typography>
          <Typography variant="caption" color="text.secondary">{job.post_type}</Typography>
        </Stack>
        <Stack direction="row" spacing={0.5} alignItems="center">
          <Typography variant="caption" color="text.secondary">{fmtWhen(job.created_at)}</Typography>
          {job.status === 'error' && (
            <Button size="small" onClick={resume} disabled={busy}
              startIcon={busy ? <CircularProgress size={12} /> : undefined}>
              Resume
            </Button>
          )}
          {job.patreon_post_id && job.status === 'done' && (
            <Button size="small" variant="outlined" onClick={() => onEdit(job)}
              sx={{ py: 0, minWidth: 0 }}>
              Edit
            </Button>
          )}
          {job.patreon_url && (
            <Link href={editUrl || job.patreon_url} target="_blank" rel="noreferrer"
              variant="caption" sx={{ whiteSpace: 'nowrap' }}>
              on Patreon ↗
            </Link>
          )}
        </Stack>
      </Stack>
      {isPartial(job.status) && (
        <LinearProgress variant="determinate" value={job.progress_pct} sx={{ mt: 0.5 }} />
      )}
      {job.note && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.3 }}>
          {job.note}
        </Typography>
      )}
      {job.status === 'error' && job.error && (
        <Typography variant="caption" color="error" sx={{ display: 'block', fontFamily: 'monospace' }}>
          {job.error.slice(0, 160)}
        </Typography>
      )}
      {resumeErr && (
        <Typography variant="caption" color="error.main" sx={{ display: 'block', mt: 0.3 }}>
          Resume failed: {resumeErr}
        </Typography>
      )}
    </Box>
  );
};

const PatreonPostsManager: React.FC<{
  defaultOpen?: boolean;
  onEdit: (seed: PatreonEditSeed) => void;
}> = ({ defaultOpen = false, onEdit }) => {
  const [open, setOpen] = useState(defaultOpen);
  const [jobs, setJobs] = useState<PatreonPostJob[]>([]);
  const [loading, setLoading] = useState(false);

  const [loadErr, setLoadErr] = useState<string | null>(null);
  const load = useCallback(async () => {
    setLoading(true); setLoadErr(null);
    try { setJobs((await patreonPostAPI.recentJobs(50)).jobs || []); }
    catch (e: any) { setLoadErr(e?.response?.data?.detail || e?.message || 'could not load posts'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { if (open && jobs.length === 0) load(); }, [open, jobs.length, load]);

  const patch = (j: PatreonPostJob) =>
    setJobs((prev) => prev.map((x) => (x.job_id === j.job_id ? j : x)));

  const partials = jobs.filter((j) => isPartial(j.status) || j.status === 'error').length;

  return (
    <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, mb: 2 }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between"
        sx={{ px: 1.5, py: 1, cursor: 'pointer' }} onClick={() => setOpen((o) => !o)}>
        <Typography variant="subtitle2">
          Your Patreon posts — record &amp; resume
          {partials > 0 && <Chip size="small" color="warning" label={`${partials} need attention`} sx={{ ml: 1, height: 18 }} />}
        </Typography>
        <Stack direction="row" alignItems="center">
          {open && (
            <IconButton size="small" onClick={(e) => { e.stopPropagation(); load(); }}>
              <RefreshIcon fontSize="small" />
            </IconButton>
          )}
          <ExpandMoreIcon sx={{ transform: open ? 'rotate(180deg)' : 'none', transition: '0.2s' }} />
        </Stack>
      </Stack>
      <Collapse in={open}>
        <Box sx={{ px: 1.5, pb: 1.5, maxHeight: 360, overflowY: 'auto' }}>
          {loading && <Box sx={{ textAlign: 'center', py: 1 }}><CircularProgress size={18} /></Box>}
          {!loading && loadErr && (
            <Typography variant="caption" color="error.main">Couldn't load posts: {loadErr}</Typography>
          )}
          {!loading && !loadErr && jobs.length === 0 && (
            <Typography variant="caption" color="text.secondary">No post attempts recorded yet.</Typography>
          )}
          {jobs.map((j) => (
            <JobRow key={j.job_id} job={j} onChange={patch}
              onEdit={(jj) => onEdit(jobToSeed(jj))} />
          ))}
        </Box>
      </Collapse>
    </Box>
  );
};

export default PatreonPostsManager;
